import { createSelector } from '@reduxjs/toolkit'
import { MenuTreeHierarchyProduct } from '@ancon/wildcat-types'
import { isDetailedProductUpSellsOutOfStock } from '@ancon/wildcat-utils/inventory'

import type { RootState } from '../../../store/rootReducer'
import { ProductReducerState } from '../types'

import productEntityAdapter from './productEntityAdapter'

function productSelector<K extends keyof ProductReducerState>(
  state: RootState,
  key: K,
) {
  return state.product[key]
}

const { selectById: productOutletMenuItemById } =
  productEntityAdapter.getSelectors()

// Simple state selector
export const productDetailsPendingSelector = (state: RootState) =>
  productSelector(state, 'fetchProductDetailsPending')

export const productHasDetailsSelector = (state: RootState) =>
  !!productSelector(state, 'productDetails')

export const productDetailsSelector = (state: RootState) =>
  productSelector(state, 'productDetails')

export const productSelectedIdSelector = (state: RootState) =>
  productSelector(state, 'selectedProductId')

export const productOutletMenuItemSelector = (state: RootState) => {
  const selectedProductId = productSelectedIdSelector(state)
  return selectedProductId
    ? productOutletMenuItemById(state.product, selectedProductId)
    : undefined
}

export const productsIsProductDetailsModalOpenSelector = (state: RootState) =>
  productSelector(state, 'isProductDetailsModalOpen')

export const productsIsProductUpsellModalOpenSelector = (state: RootState) =>
  productSelector(state, 'isUpsellGroupModalOpen')

export const isProductSearchModalOpenSelector = (state: RootState) =>
  productSelector(state, 'isProductSearchModalOpen')

export const productsDetailsProductIdSelector = (state: RootState) =>
  productDetailsSelector(state)?.productId

export const productsDetailsProductNameSelector = createSelector(
  productDetailsSelector,
  productDetails =>
    productDetails?.customerFacingName || productDetails?.name || '',
)

export const productsDetailsDescriptionSelector = (state: RootState) =>
  productDetailsSelector(state)?.description

export const productsDetailsAllergyInfoSelector = (state: RootState) =>
  productDetailsSelector(state)?.allergyInformation

export const productsDetailsHasVariantsSelector = (state: RootState) =>
  productDetailsSelector(state)?.hasVariants

export const productsDetailsImageIdSelector = (state: RootState) =>
  productDetailsSelector(state)?.imageId

export const productsDetailsVariantsSelector = (state: RootState) =>
  productDetailsSelector(state)?.variants

export const productsDetailsIngredientsSelector = (state: RootState) =>
  productDetailsSelector(state)?.ingredients

export const productsDetailsAllowDynamicPricingSelector = (state: RootState) =>
  productDetailsSelector(state)?.allowDynamicPricing

export const productsDetailsAddonGroupsSelector = (state: RootState) =>
  productDetailsSelector(state)?.addOnGroups

export const productsDetailsMaximumIngredientsForDynamicAddonSelector = (
  state: RootState,
) => productDetailsSelector(state)?.maximumIngredientsForDynamicAddons

export const productDetailsUpsellGroupsSelector = (state: RootState) =>
  productDetailsSelector(state)?.upsellGroups

export const productsDetailsUpsellGroupProductsSelector = (
  state: RootState,
) => {
  const upsellGroups = productDetailsUpsellGroupsSelector(state)

  if (upsellGroups && upsellGroups.length > 0) {
    return upsellGroups[0].products
  }

  return []
}

export const productsDetailsUpsellGroupNameSelector = createSelector(
  productDetailsUpsellGroupsSelector,
  upsellGroups => {
    if (upsellGroups && upsellGroups.length > 0) {
      return upsellGroups[0].name
    }

    return null
  },
)

export const productsUpsellGroupHasProductsSelector = (state: RootState) =>
  !!productsDetailsUpsellGroupProductsSelector(state)?.length

export const productsUpSellGroupIsOutOfStockSelector = (state: RootState) => {
  const productUpSellGroups = productDetailsUpsellGroupsSelector(state)
  return isDetailedProductUpSellsOutOfStock(productUpSellGroups)
}

export const productDetailsVariantByIdSelector = createSelector(
  [productDetailsSelector, (_, variantId: string | null) => variantId],
  (productDetails, variantId) =>
    variantId ? productDetails?.variants.find(v => v.id === variantId) : null,
)

// Adapter selectors
const productEntityAdapterSelectors = productEntityAdapter.getSelectors(
  (state: RootState) => state.product,
)

export const {
  selectById: productByIdSelector,
  selectEntities: productEntitiesSelector,
} = productEntityAdapterSelectors

export const productSelectedMenuGroupProductsSelector = createSelector(
  [productEntitiesSelector, (_, productIds: string[]) => productIds],
  (productEntities, ids) =>
    ids.reduce((acc, id) => {
      const product = productEntities[id]
      if (product) {
        acc.push(product)
      }
      return acc
    }, [] as MenuTreeHierarchyProduct[]),
)
